import { Solution } from "@gen/travel/v1/search_pb";
import { CabinClass } from "@pages/booking/search/Search";
import { LocalDateTime, Offer } from "@gen/travel/v1/common_pb";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";
import { localDateTimeToDate } from "@utils/date";
export type FiltersState = {
  directJourney: boolean;
  flexibility: string[];
  sortBy: string;
  priceRange: number[];
};

export const isFilteredSolution = (
  solution: Solution | ExchangeSolution,
  filters: FiltersState,
  cabinClass?: CabinClass,
): boolean => {
  const offers = solution.offers.filter(
    (offer) => !cabinClass || offer.cabinClass.value === cabinClass,
  );

  if (filters.directJourney) {
    if (solution.journey?.segments && solution.journey?.segments.length > 1)
      return false;
  }

  if (filters?.flexibility?.length > 0) {
    const hasFlexibility = offers.some((offer) =>
      filters.flexibility.includes(offer.flexibility.toString()),
    );
    if (!hasFlexibility) return false;
  }

  if (
    filters.priceRange &&
    filters.priceRange[0] != 0 &&
    filters.priceRange[1] != 0
  ) {
    const [minPrice, maxPrice] = filters.priceRange;

    const hasOfferInRange = offers.some((offer) => {
      const price = offer.initialPrice?.amount ?? Infinity;
      return Number(price) >= minPrice * 100 && Number(price) <= maxPrice * 100;
    });

    if (!hasOfferInRange) return false;
  }

  return true;
};

export const isFilteredOffer = (
  offer: Offer | ExchangeOffer,
  filters: FiltersState,
) => {
  if (filters?.flexibility?.length > 0) {
    const hasFlexibility = filters.flexibility.includes(
      offer.flexibility.toString(),
    );
    if (!hasFlexibility) return false;
  }

  if (
    filters.priceRange &&
    filters.priceRange[0] != 0 &&
    filters.priceRange[1] != 0
  ) {
    const [minPrice, maxPrice] = filters.priceRange;
    const hasOfferInRange =
      Number(offer.initialPrice?.amount) >= minPrice * 100 &&
      Number(offer.initialPrice?.amount) <= maxPrice * 100;

    if (!hasOfferInRange) return false;
  }

  return true;
};

export const sortSolutions = (
  solutions: Solution[] | ExchangeSolution[],
  filters: FiltersState,
) => {
  const getLowestPrice = (
    offers: { initialPrice?: { amount?: number | bigint } }[] = [],
  ) =>
    offers.reduce((min, offer) => {
      const amount = offer.initialPrice?.amount;
      if (amount != null) {
        return Math.min(
          min,
          typeof amount === "bigint" ? Number(amount) : amount,
        );
      }
      return min;
    }, Infinity);

  const getDuration = (journey?: {
    schedule?: {
      departure?: LocalDateTime;
      arrival?: LocalDateTime;
    };
  }) => {
    if (journey?.schedule?.departure && journey?.schedule?.arrival) {
      const departure = localDateTimeToDate(journey.schedule.departure);
      const arrival = localDateTimeToDate(journey.schedule.arrival);
      return arrival.getTime() - departure.getTime();
    }
    return Infinity;
  };

  return [...solutions].sort((a, b) => {
    if (filters.sortBy === "price") {
      return getLowestPrice(a.offers) - getLowestPrice(b.offers);
    } else if (filters.sortBy === "time") {
      const timeA = a.journey?.schedule?.departure
        ? localDateTimeToDate(a.journey.schedule.departure).getTime()
        : Infinity;
      const timeB = b.journey?.schedule?.departure
        ? localDateTimeToDate(b.journey.schedule.departure).getTime()
        : Infinity;
      return timeA - timeB;
    } else if (filters.sortBy === "duration") {
      return getDuration(a.journey) - getDuration(b.journey);
    } else if (filters.sortBy === "lowestEmissions") {
      return (
        (a.journey?.ghgEmission ?? Infinity) -
        (b.journey?.ghgEmission ?? Infinity)
      );
    }
    return 0;
  });
};
